import axios from 'axios'
import { StatusCodes } from 'http-status-codes'
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type { DealEntity, UpdateDealReqBody } from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'
import { useToast } from '../../../hooks/useToast'

import { DEALS_QUERY_KEY_ROOT } from './useDealsQuery'
import { dealQueryKey } from './useDealQuery'

interface UpdateVariables {
  dealId: string
  body: UpdateDealReqBody
}

export function useUpdateDealMutation(): UseMutationResult<DealEntity, unknown, UpdateVariables> {
  const queryClient = useQueryClient()
  const toast = useToast()
  const handleError = useApiErrorHandler()

  return useMutation<DealEntity, unknown, UpdateVariables>(
    ({ dealId, body }) => dealsAPI.updateDeal(dealId, body),
    {
      onSuccess: (deal) => {
        toast.current?.show({
          severity: 'success',
          summary: 'Deal updated',
          detail: `Saved changes to ${deal.orderNumber}.`,
          life: 3000,
        })
        queryClient.invalidateQueries({ queryKey: DEALS_QUERY_KEY_ROOT })
        queryClient.invalidateQueries({ queryKey: dealQueryKey(deal.id) })
      },
      onError: (error, { dealId }) => {
        if (
          axios.isAxiosError(error) &&
          error.response?.status === StatusCodes.CONFLICT
        ) {
          // 409 from PATCH /deals/:id signals lifecycle conflict (deal got
          // canceled/archived under us). Re-fetch so the modal's lock banner
          // can apply on the next render.
          queryClient.invalidateQueries({ queryKey: dealQueryKey(dealId) })
        }
        handleError(error)
      },
    },
  )
}
